import {
  Paper,
  Grid,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  MenuItem,
  IconButton,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  useMediaQuery,
  Autocomplete,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { useSnackbar } from 'notistack';
import tenantGroupService from './tenantGroupService';
import tenantService from '../tenants/tenantService';
import publisherService from '../../services/publisherService';
import useImageSelect from '../../hooks/useImageSelect';

const emptyMember = {
  id: '',
  tenantId: '',
  displayName: '',
  deepLinkSlug: '',
  sortOrder: 0,
  isDefaultCandidate: false,
  shortcutIconUrl: '',
  shortcutIcon: null as any,
};

const slugify = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/** Prefer square crop for home-screen / picker icons. */
const shortcutUrlFromImage = (image: any): string =>
  image?.url1_1 ||
  image?.Url1_1 ||
  image?.thumbnailUrl ||
  image?.ThumbnailUrl ||
  image?.originalUrl ||
  image?.OriginalUrl ||
  '';

const imagePreviewFromUrl = (url: string) =>
  url
    ? {
        id: 0,
        originalUrl: url,
        thumbnailUrl: url,
        url1_1: url,
        fileName: 'shortcut-icon.png',
      }
    : null;

const TenantGroupEdit = () => {
  const { id } = useParams();
  const isCreate = !id || id === 'create';
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const { enqueueSnackbar } = useSnackbar();
  const isSmallScreen = useMediaQuery((theme: any) =>
    theme.breakpoints.down('sm')
  );
  const { ImageSelectComponent } = useImageSelect();

  const [form, setForm] = useState({
    name: '',
    slug: '',
    description: '',
    isActive: true,
    suggestionMode: 0,
  });
  const [members, setMembers] = useState<any[]>([]);
  const [tenants, setTenants] = useState<any[]>([]);
  const [publishers, setPublishers] = useState<any[]>([]);
  const [publisherId, setPublisherId] = useState<number | null>(null);
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [memberForm, setMemberForm] = useState<any>(emptyMember);
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);
  const [slugTouched, setSlugTouched] = useState(false);

  const deepLinkPreview = useMemo(() => {
    if (!memberForm.deepLinkSlug) return '';
    return `…/tenant/${memberForm.deepLinkSlug}`;
  }, [memberForm.deepLinkSlug]);

  const load = () => {
    if (isCreate) return;
    dispatch({ type: 'SHOW_QUERY' });
    tenantGroupService
      .get(id!)
      .then((response) => {
        if (response.hasError) {
          enqueueSnackbar('Nem található a csoport', { variant: 'error' });
          navigate('/tenant-groups');
          return;
        }
        const g = response.result;
        setForm({
          name: g.name || '',
          slug: g.slug || '',
          description: g.description || '',
          isActive: g.isActive !== false,
          suggestionMode: g.suggestionMode ?? 0,
        });
        setSlugTouched(true);
        setMembers(g.members || []);
      })
      .finally(() => dispatch({ type: 'HIDE' }));
  };

  useEffect(() => {
    load();
    tenantService.list().then((response) => {
      if (!response.hasError) setTenants(response.records ?? []);
    });
    publisherService.list().then((response) => {
      if (response?.hasError) return;
      const rows = response.records ?? response ?? [];
      setPublishers(Array.isArray(rows) ? rows : []);
      const first = (Array.isArray(rows) ? rows : [])[0];
      const firstId = first?.id ?? first?.Id;
      if (firstId != null) setPublisherId(Number(firstId));
    });
  }, [id]);

  const applyShortcutImage = (image: any) => {
    const url = shortcutUrlFromImage(image);
    setMemberForm((prev: any) => ({
      ...prev,
      shortcutIconUrl: url,
      shortcutIcon: image,
    }));
  };

  const clearShortcutImage = () => {
    setMemberForm((prev: any) => ({
      ...prev,
      shortcutIconUrl: '',
      shortcutIcon: null,
    }));
  };

  const saveGroup = async () => {
    if (!form.name.trim() || !form.slug.trim()) {
      enqueueSnackbar('A név és a slug kötelező.', { variant: 'warning' });
      return;
    }
    dispatch({ type: 'SHOW_SAVE' });
    try {
      if (isCreate) {
        const response = await tenantGroupService.create(form);
        if (response.hasError) {
          enqueueSnackbar(response.errorMessages?.join(',') || 'Hiba', {
            variant: 'error',
          });
          return;
        }
        enqueueSnackbar('Csoport létrehozva', { variant: 'success' });
        navigate(`/tenant-groups/edit/${response.result.id}`);
      } else {
        const response = await tenantGroupService.update(id!, form);
        if (response.hasError) {
          enqueueSnackbar(response.errorMessages?.join(',') || 'Hiba', {
            variant: 'error',
          });
          return;
        }
        enqueueSnackbar('Mentve', { variant: 'success' });
        load();
      }
    } finally {
      dispatch({ type: 'HIDE' });
    }
  };

  const openAddMember = () => {
    setEditingMemberId(null);
    setMemberForm({ ...emptyMember });
    setMemberDialogOpen(true);
  };

  const openEditMember = (m: any) => {
    setEditingMemberId(m.id);
    const url = m.shortcutIconUrl || '';
    setMemberForm({
      ...m,
      shortcutIcon: m.shortcutIcon ?? imagePreviewFromUrl(url),
    });
    setMemberDialogOpen(true);
  };

  const saveMember = async () => {
    if (!id || isCreate) return;
    dispatch({ type: 'SHOW_SAVE' });
    try {
      const response = editingMemberId
        ? await tenantGroupService.updateMember(id, editingMemberId, memberForm)
        : await tenantGroupService.addMember(id, memberForm);
      if (response.hasError) {
        enqueueSnackbar(
          response.errorMessages?.join(',') || response.message || 'Hiba',
          { variant: 'error' }
        );
        return;
      }
      setMemberDialogOpen(false);
      load();
    } finally {
      dispatch({ type: 'HIDE' });
    }
  };

  const deleteMember = async (memberId: string) => {
    if (!id) return;
    await tenantGroupService.deleteMember(id, memberId);
    load();
  };

  return (
    <>
      <Paper>
        <Grid container spacing={2} sx={{ justifyContent: 'left', p: 5 }}>
          <h2>{isCreate ? 'Új bérlőcsoport' : 'Bérlőcsoport szerkesztése'}</h2>

          <Grid size={12}>
            <TextField
              fullWidth
              required
              label="Név"
              value={form.name}
              onChange={(e) => {
                const name = e.target.value;
                setForm((prev) => ({
                  ...prev,
                  name,
                  slug: slugTouched ? prev.slug : slugify(name),
                }));
              }}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              required
              label="Slug"
              value={form.slug}
              onChange={(e) => {
                setSlugTouched(true);
                setForm((prev) => ({ ...prev, slug: slugify(e.target.value) }));
              }}
              helperText="Csoport azonosító (URL-barát). A mobil deeplink a tag Deep link slugját használja."
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              label="Leírás"
              multiline
              minRows={2}
              value={form.description}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, description: e.target.value }))
              }
            />
          </Grid>

          <Grid size={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, isActive: e.target.checked }))
                  }
                />
              }
              label="Aktív"
            />
          </Grid>

          <Grid size={12}>
            <TextField
              select
              fullWidth
              label="Javaslat mód (aggregátor mobil)"
              value={form.suggestionMode}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  suggestionMode: Number(e.target.value),
                }))
              }
              helperText={
                form.suggestionMode === 1
                  ? 'A mobil a tagok térképpontjához (bérlő MapCenter) viszonyítva javasol városokat GPS alapján. GPS hiányában az alapértelmezett tag érvényesül.'
                  : 'A „Default” jelölésű tag kerül előre a városválasztóban.'
              }
            >
              <MenuItem value={0}>Alapértelmezett megjelölés</MenuItem>
              <MenuItem value={1}>Helymeghatározás</MenuItem>
            </TextField>
          </Grid>

          <Grid container sx={{ pt: 3, justifyContent: 'left' }}>
            <Grid
              size={{ xs: 12, sm: isSmallScreen ? 12 : 'auto' }}
              sx={{ p: 1 }}
            >
              <Button
                variant="contained"
                color="primary"
                fullWidth={isSmallScreen}
                onClick={saveGroup}
              >
                Mentés
              </Button>
            </Grid>
            <Grid
              size={{ xs: 12, sm: isSmallScreen ? 12 : 'auto' }}
              sx={{ p: 1 }}
            >
              <Button
                variant="outlined"
                color="secondary"
                fullWidth={isSmallScreen}
                onClick={() => navigate('/tenant-groups')}
              >
                Mégse
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {!isCreate && (
        <Paper sx={{ mt: 2 }}>
          <Grid container spacing={2} sx={{ justifyContent: 'left', p: 5 }}>
            <Grid
              container
              size={12}
              sx={{ justifyContent: 'space-between', alignItems: 'center' }}
            >
              <h2 style={{ margin: 0 }}>Tagok</h2>
              <Tooltip title="Tag hozzáadása">
                <IconButton color="primary" onClick={openAddMember}>
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid size={12}>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={56}>Ikon</TableCell>
                      <TableCell>Megjelenített név</TableCell>
                      <TableCell>Bérlő</TableCell>
                      <TableCell>Deep link slug</TableCell>
                      <TableCell>Sorrend</TableCell>
                      <TableCell>Default</TableCell>
                      <TableCell width={100} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {members.map((m) => (
                      <TableRow key={m.id}>
                        <TableCell>
                          {m.shortcutIconUrl ? (
                            <Box
                              component="img"
                              src={m.shortcutIconUrl}
                              alt=""
                              sx={{
                                width: 36,
                                height: 36,
                                objectFit: 'cover',
                                borderRadius: 1,
                              }}
                            />
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>{m.displayName}</TableCell>
                        <TableCell>{m.tenantName || m.tenantId}</TableCell>
                        <TableCell>{m.deepLinkSlug}</TableCell>
                        <TableCell>{m.sortOrder}</TableCell>
                        <TableCell>
                          {m.isDefaultCandidate ? 'Igen' : ''}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => openEditMember(m)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => deleteMember(m.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {members.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6}>Nincs tag</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Dialog
        open={memberDialogOpen}
        onClose={() => setMemberDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {editingMemberId ? 'Tag szerkesztése' : 'Új tag'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={12}>
              <Autocomplete
                options={tenants}
                getOptionLabel={(t: any) => t.name ?? t.Name ?? ''}
                value={
                  tenants.find(
                    (t) => (t.id ?? t.Id) === memberForm.tenantId
                  ) ?? null
                }
                onChange={(_e, tenant) => {
                  const tenantId = tenant ? (tenant.id ?? tenant.Id) : '';
                  const name = tenant?.name ?? tenant?.Name ?? '';
                  setMemberForm((prev: any) => ({
                    ...prev,
                    tenantId,
                    displayName: prev.displayName || name,
                    deepLinkSlug: prev.deepLinkSlug || slugify(name),
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Bérlő"
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                required
                label="Megjelenített név"
                value={memberForm.displayName}
                onChange={(e) => {
                  const displayName = e.target.value;
                  setMemberForm((prev: any) => ({
                    ...prev,
                    displayName,
                    deepLinkSlug:
                      prev.deepLinkSlug &&
                      prev.deepLinkSlug !== slugify(prev.displayName)
                        ? prev.deepLinkSlug
                        : slugify(displayName),
                  }));
                }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                required
                label="Deep link slug"
                value={memberForm.deepLinkSlug}
                onChange={(e) =>
                  setMemberForm((prev: any) => ({
                    ...prev,
                    deepLinkSlug: slugify(e.target.value),
                  }))
                }
                helperText={
                  deepLinkPreview
                    ? `Mobil deeplink path: ${deepLinkPreview}`
                    : 'Picker / kezdőképernyő ikon deeplink szegmens'
                }
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                type="number"
                label="Sorrend"
                value={memberForm.sortOrder}
                onChange={(e) =>
                  setMemberForm((prev: any) => ({
                    ...prev,
                    sortOrder: Number(e.target.value) || 0,
                  }))
                }
              />
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Shortcut ikon (picker / Android kezdőképernyő)
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Galériából választás vagy új feltöltés. Mentéskor a kép URL-je
                kerül tárolásra (négyzetes vágás előnyben).
              </Typography>
              {publishers.length > 0 && (
                <Autocomplete
                  sx={{ mb: 1 }}
                  options={publishers.map((p) => p.id ?? p.Id)}
                  value={publisherId}
                  onChange={(_, value) =>
                    setPublisherId(value != null ? Number(value) : null)
                  }
                  getOptionLabel={(option) =>
                    publishers.find((p) => (p.id ?? p.Id) === option)?.name ??
                    publishers.find((p) => (p.id ?? p.Id) === option)?.Name ??
                    ''
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Galéria (kiadó)"
                      fullWidth
                    />
                  )}
                />
              )}
              <ImageSelectComponent
                image={memberForm.shortcutIcon}
                onSelect={applyShortcutImage}
                onUploaded={applyShortcutImage}
                alt="shortcut-icon"
                publisherId={publisherId ?? 1}
                currentFolderId="-1"
              />
              {!!memberForm.shortcutIconUrl && (
                <Button
                  sx={{ mt: 1 }}
                  size="small"
                  color="secondary"
                  onClick={clearShortcutImage}
                >
                  Ikon eltávolítása
                </Button>
              )}
            </Grid>
            <Grid size={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!memberForm.isDefaultCandidate}
                    onChange={(e) =>
                      setMemberForm((prev: any) => ({
                        ...prev,
                        isDefaultCandidate: e.target.checked,
                      }))
                    }
                  />
                }
                label="Alapértelmezett jelölt"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMemberDialogOpen(false)}>Mégse</Button>
          <Button variant="contained" onClick={saveMember}>
            Mentés
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TenantGroupEdit;
